import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactLoader from 'components/Loader';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  BsFillImageFill,
  BsCameraVideoFill,
  BsChatDotsFill
} from 'react-icons/bs';
import { AiFillFilePdf } from 'react-icons/ai';
import {
  Container,
  Row,
  Card,
  Col,
  Input,
  CardHeader,
  CardBody,
  Form
} from 'reactstrap';
import { useForm, Controller, useWatch } from 'react-hook-form';

import TeamService from '../service/index';
import { custom_select } from 'assets/styles/multiple_select';

import ReactButton from 'components/Buttons/ReactButton';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import { ErrorMessage } from '@hookform/error-message';
import { SelectTemplateOptions } from 'views/ChatBroadcastList/form/styled';
import Select from 'react-select';
import FlexChatIntegration from 'views/ServicePanel/components/QueueContent/components/Contact/service/FlexChatIntegration';
import FlexSelect from 'components/Inputs/FlexSelect';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { SearchTemplateWhatsapp } from 'views/ServicePanel/components/QueueContent/components/Contact/Function';
import { SiWhatsapp } from 'react-icons/si';
import { BoxSelectFlex } from 'views/ChatBroadcastList/form/styled';

export default function TeamForm(props) {
  const {
    reset,
    control,
    handleSubmit,
    setValue,
    errors,
    watch,
    getValues,
    formState
  } = useForm();
  const { isDirty } = formState;
  const { userList: availableUsers } = useUsers();
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const [whatsappChannels, setWhatsappChannels] = useState([]);
  const whatsappOptions = whatsappChannels.map((channel) => ({
    value: channel.source,
    label: channel.nome_canal
  }));

  const selectedWhatsappChannels =
    useWatch({ control, name: 'whatsappsHasAccess' }) ?? [];

  const [templatesByChannel, setTemplatesByChannel] = useState({});

  const templateOptions = useMemo(() => {
    const optionsPerChannel = selectedWhatsappChannels.map((channel) => {
      return templatesByChannel[channel] ?? [];
    });
    return [].concat(...optionsPerChannel);
  }, [selectedWhatsappChannels, templatesByChannel]);

  useEffect(() => {
    const channelsToFetch = selectedWhatsappChannels.filter(
      (channel) => !Object.keys(templatesByChannel).includes(channel)
    );

    if (channelsToFetch.length === 0) return;

    const fetchTemplatesForChannels = async () => {
      for (const channel of channelsToFetch) {
        try {
          const response = await FlexChatIntegration.getContatoAtivoByPhone(
            channel,
            false
          );
          const updatedTemplates = response.data.data.map((item) => ({
            ...item,
            label: item.description,
            value: `${item.id}@${item.whatsapp_source}`,
            whatsapp_source: item.whatsapp_source
          }));

          setTemplatesByChannel((prevTemplates) => ({
            ...prevTemplates,
            [channel]: updatedTemplates
          }));
        } catch (error) {
          toast.error(`Erro ao buscar templates para o canal ${channel}`);
        }
      }
    };

    fetchTemplatesForChannels();
  }, [selectedWhatsappChannels, templatesByChannel]);

  useEffect(() => {
    if (!initialDataLoaded) return;

    const currentTemplateSelection = getValues('templateHasAccess') || [];

    if (currentTemplateSelection.length === 0) return;
  }, [templateOptions, getValues, setValue, initialDataLoaded]);

  const [loading, setLoading] = useState(false);
  const { id } = props.match.params;

  const iconMap = {
    IMAGE: <BsFillImageFill />,
    VIDEO: <BsCameraVideoFill />,
    DOCUMENT: <AiFillFilePdf />,
    TEXT: <BsChatDotsFill />
  };

  const colorMap = {
    DOCUMENT: '#d92222d1',
    IMAGE: '#2dce89',
    VIDEO: '#11cdef',
    TEXT: 'rgb(54 54 96)'
  };

  const fetchWhatsappChannels = () => {
    FlexChatIntegration.getAllChannels()
      .then((res) => {
        setWhatsappChannels(res?.data?.data?.whatsapp ?? []);
      })
      .catch(() => {
        toast.error('Erro ao buscar whatsapp');
      });
  };

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await TeamService.get(id);

      const { data: teamData } = response?.data;
      const { whatsapps: teamWhastsapps } = response?.data?.data ?? {};

      const templatesPerChannelFromTeam = {};
      for (const channel of teamWhastsapps) {
        if (Object.keys(templatesByChannel).includes(channel.source)) {
          continue;
        }
        const responseTemplates =
          await FlexChatIntegration.getContatoAtivoByPhone(
            channel.source,
            false
          );

        templatesPerChannelFromTeam[channel.source] =
          responseTemplates.data.data.map((item) => ({
            ...item,
            label: item.description,
            value: `${item.id}@${item.whatsapp_source}`,
            whatsapp_source: item.whatsapp_source
          }));
      }

      setTemplatesByChannel((prev) => ({
        ...prev,
        ...templatesPerChannelFromTeam
      }));

      if (teamData) {
        const formattedTemplates = teamData?.templates?.map((template) => ({
          ...template,
          value: `${template.id}@${template.whatsapp_source}`,
          label: template.description,
          templateType: template.type,
          whatsapp_source: template.whatsapp_source
        }));

        const formattedTeam = {
          name: teamData?.name,
          templateHasAccess: formattedTemplates || [],
          whatsappsHasAccess: teamData?.whatsapps?.map((canal) => canal.source),
          userLogins: teamData?.users?.map((user) => ({
            value: user.login,
            label: user.name
          }))
        };

        setTimeout(() => {
          reset(formattedTeam);
          setInitialDataLoaded(true);
        }, 0);
      }
    } catch (error) {
      toast.error('Ocorreu um erro ao carregar os dados');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  const fetchSynchronizedTemplates = useCallback(async () => {
    setLoading(true);
    await FlexChatIntegration.syncTemplates();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (id) {
      fetchTeamData();
    } else {
      setInitialDataLoaded(true);
    }
  }, [id, fetchTeamData]);

  useEffect(() => {
    fetchSynchronizedTemplates();
  }, [fetchSynchronizedTemplates]);

  useEffect(() => {
    fetchWhatsappChannels();
  }, []);

  const history = useHistory();
  const goToTeamList = () => history.push('/admin/Team/list');

  const onFormSubmit = async (formData) => {
    if (!formData?.userLogins?.length) {
      toast.error('É necessário selecionar pelo menos um usuário');
      return;
    }
    const payload = {
      ...formData,
      templateHasAccess: formData?.templateHasAccess?.map?.((template) => {
        const [templateId, whatsappSource] = template.value.split('@');
        return { templateId, whatsappSource };
      }),
      userLogins: formData?.userLogins?.map?.((user) => user.value) || []
    };

    try {
      if (id) {
        await TeamService.put(id, payload);
        toast.info('Equipe foi atualizada com sucesso!');
      } else {
        await TeamService.post(payload);
        toast.success('Equipe foi cadastrada com sucesso!');
      }
      history.push('/admin/Team/list');
    } catch (error) {
      toast.error('Ocorreu um erro ao cadastrar o time');
    }
  };

  const generateUserOptions = useCallback(() => {
    if (!availableUsers) return [];
    return availableUsers.map((user) => ({
      value: user.login,
      label: user.name
    }));
  }, [availableUsers]);

  const renderErrorMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  useEffect(() => {
    if (!initialDataLoaded) return;

    const currentTemplateSelection = getValues('templateHasAccess') || [];
    if (currentTemplateSelection.length === 0) return;

    const filteredSelection = currentTemplateSelection.filter((template) => {
      const templateSource =
        template.whatsapp_source || template.value.split('@')[1];

      return selectedWhatsappChannels.includes(templateSource);
    });

    if (filteredSelection.length !== currentTemplateSelection.length) {
      setValue('templateHasAccess', filteredSelection);
    }
  }, [selectedWhatsappChannels, getValues, setValue, initialDataLoaded]);

  useEffect(() => {
    const unlisten = history.listen((_, action) => {
      if (action === 'POP') {
        history.replace('/admin/Team/list');
      }
    });

    return () => unlisten();
  }, [history]);

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/Team/list"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message="Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar"
      />
      <Container className="mt--6" fluid>
        <Row className="mt-3 mx-2">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                    alignItems: 'center'
                  }}
                >
                  <h3>Equipes</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onFormSubmit, console.log)}
                    >
                      <Row className="mb-3">
                        <Col md="4">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            rules={{ required: 'Nome é obrigatório!' }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) =>
                              renderErrorMessage(message)
                            }
                          />
                        </Col>
                        <Col md="4">
                          <label className="form-control-label">Membros*</label>
                          <Controller
                            render={() => (
                              <Select
                                styles={{ ...custom_select, width: '100%' }}
                                placeholder="Selecione os membros da Equipe"
                                options={generateUserOptions() || []}
                                isMulti
                                isClearable
                                closeMenuOnSelect={false}
                                valueController={setValue}
                                name="userLogins"
                                value={watch('userLogins') || null}
                                onChange={(value) => {
                                  setValue('userLogins', value);
                                }}
                                defaultValue=""
                                rules={{ required: 'Nome é obrigatório!' }}
                              />
                            )}
                            control={control}
                            name="userLogins"
                            defaultValue=""
                          />
                        </Col>
                        <Col md="4">
                          <label className="form-control-label">Whatsapp</label>
                          <Controller
                            render={(fieldProps) => (
                              <FlexSelect
                                isClearable
                                dataOptions={whatsappOptions || []}
                                isMulti
                                closeMenuOnSelect
                                value={fieldProps.value}
                                valueController={setValue}
                                fieldName="whatsappsHasAccess"
                                labelName="label"
                                valueName="value"
                              />
                            )}
                            control={control}
                            name="whatsappsHasAccess"
                            defaultValue={[]}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="whatsappsHasAccess"
                            render={({ message }) =>
                              renderErrorMessage(message)
                            }
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col>
                          <label className="form-control-label">
                            Templates que essa equipe tem acesso
                          </label>
                          <Controller
                            render={(fieldProps) => (
                              <Select
                                styles={{ ...custom_select, width: '100%' }}
                                placeholder="Selecione o(s) template(s)..."
                                options={templateOptions || []}
                                isSearchable
                                isClearable={false}
                                isMulti
                                name="templateHasAccess"
                                valueController={setValue}
                                value={fieldProps.value || null}
                                filterOption={(option, inputValue) =>
                                  SearchTemplateWhatsapp({
                                    inputValue,
                                    option
                                  })
                                }
                                onChange={(selectedTemplates) => {
                                  setValue(
                                    'templateHasAccess',
                                    selectedTemplates
                                  );
                                }}
                                getOptionValue={(optionValue) =>
                                  optionValue.value
                                }
                                getOptionLabel={(option) => (
                                  <SelectTemplateOptions
                                    isMulti
                                    color={colorMap[option.type]}
                                    title={option.label}
                                  >
                                    <BoxSelectFlex>
                                      {iconMap[option.type] ?? '!'}
                                      {option.name}
                                    </BoxSelectFlex>
                                    <BoxSelectFlex>
                                      <SiWhatsapp />
                                      {option.whatsapp_source}
                                    </BoxSelectFlex>
                                  </SelectTemplateOptions>
                                )}
                                defaultValue=""
                              />
                            )}
                            control={control}
                            name="templateHasAccess"
                            defaultValue=""
                          />
                        </Col>
                      </Row>
                      <Row className="mt-5 ml-1">
                        <ReactButton btnColor="confirmation">
                          Salvar
                        </ReactButton>
                        <ReactButton
                          type="button"
                          btnColor="cancelation"
                          onClick={goToTeamList}
                        >
                          Cancelar
                        </ReactButton>
                      </Row>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
}
